import { TextWithHighlight } from "../../../../common/components/TextWithHighlight";
import { Autocomplete } from "../../../../common/components/Select/AutocompleteSelect";
import { type FocusEventHandler, useMemo } from "react";
import { useSimpleAutocompleteStyles } from "../../../../common/components/Select/SimpleAutocompleteSelect";
import { getInchSuggestions, type InchSuggestion } from "./getInchSuggestions";
import { Controller, useFormContext } from "react-hook-form";
import { getEmptyList } from "../../../../utils/misc";
import { useTranslation } from "react-i18next";
import { VALID_INPUT_NUMBER } from "~/utils/validations/isNumber";
import { NumberSystem } from "~/HousingSelectionAssistant/types/types";
import { createFilterOptions } from "@mui/material";

interface DualSystemNumberInputProps {
  className?: string;
  mode: NumberSystem;
  onChange?: (value: string) => void;
  value?: string;
  placeholder?: string;
  label?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onBlur?: FocusEventHandler<any>;
  "data-test-id"?: string;
}

export const DualSystemNumberInput = ({ value, onChange, mode, ...rest }: DualSystemNumberInputProps) => {
  const classes = useSimpleAutocompleteStyles();
  const { t } = useTranslation();
  const onChangeCb = useMemo(() => makeOnChangeCb(mode, onChange), [onChange, mode]);

  return (
    <Autocomplete
      loadingText={t("spinner.text")}
      disableClearable={true}
      autoHighlight={true}
      classes={classes}
      freeSolo={true}
      inputValue={value}
      getOptionLabel={getOptionLabel}
      filterOptions={filterOptions}
      onInputChange={onChangeCb}
      onChange={onChangeCb}
      options={mode === NumberSystem.Imperial ? getInchSuggestions() : getEmptyList<InchSuggestion>()}
      renderOption={(props, value: InchSuggestion) => {
        return <TextWithHighlight highlight={"inputValue"}>{value.label}</TextWithHighlight>;
      }}
      {...rest}
    />
  );
};

const filterOptions = createFilterOptions<InchSuggestion>({
  stringify: (option) => option.label,
  trim: true,
});

interface RHFDualSystemNumberInputProps {
  name: string;
  className?: string;
  mode: NumberSystem;
  placeholder?: string;
  label?: string;
  "data-test-id"?: string;
}

export const RHFDualSystemNumberInput = ({ name, ...rest }: RHFDualSystemNumberInputProps) => {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { value, onChange, onBlur } }) => (
        <DualSystemNumberInput value={value} onChange={onChange} onBlur={onBlur} {...rest} />
      )}
    />
  );
};

const getOptionLabel = (value: InchSuggestion | string) => (typeof value === "string" ? value : value.label);

const makeOnChangeCb =
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (mode: NumberSystem, onChange?: (val: string) => void) => (e: any, value: InchSuggestion | string) => {
    const val = typeof value === "string" ? value : value.stringValue;
    const regex = VALID_INPUT_NUMBER;
    if (!regex.test(val?.trim())) {
      return;
    }
    onChange && onChange(val);
  };
